import { FreespinScatterResult } from "ags/integration/model/results/freespin-scatter-result";
import { SparkResult } from "ags/integration/model/results/spark-result";
import { AbstractComponent } from "appworks/components/abstract-component";
import { Components } from "appworks/components/components";
import { Layers } from "appworks/graphics/layers/layers";
import { gameState } from "appworks/model/game-state";
import { Result } from "appworks/model/gameplay/records/results/result";
import { CurrencyService } from "appworks/services/currency/currency-service";
import { Services } from "appworks/services/services";
import { TranslationsService } from "appworks/services/translations/translations-service";
import { Contract } from "appworks/utils/contracts/contract";
import { Parallel } from "appworks/utils/contracts/parallel";
import { CelebrateWinComponent } from "slotworks/components/celebrate-win/celebrate-win-component";
import { FooterComponent } from "slotworks/components/footer/footer-component";
import { MatrixComponent } from "slotworks/components/matrix/matrix-component";
import { SymbolSubcomponent } from "slotworks/components/matrix/symbol/symbol-subcomponent";
import { BonusResult } from "slotworks/model/gameplay/records/results/bonus-result";
import { FreespinWinResult } from "slotworks/model/gameplay/records/results/freespin-win-result";
import { LineResult } from "slotworks/model/gameplay/records/results/line-result";
import { ScatterResult } from "slotworks/model/gameplay/records/results/scatter-result";
import { SymbolWinResult } from "slotworks/model/gameplay/records/results/symbol-win-result";
import { WaysResult } from "slotworks/model/gameplay/records/results/ways-result";
import { SpinRecord } from "slotworks/model/gameplay/records/spin-record";
import { slotDefinition } from "slotworks/model/slot-definition";

export class LoopWinsComponent extends AbstractComponent {

    public static minCelebrationDuration = 1000;

    protected wins: any[] = [];
    protected currentWin: number;
    protected startContract: Contract<void>;

    public start(): Contract<void> {
        this.wins = this.getWinsToAnimate();
        this.wins.forEach((win) => {
            if (!win.positions) { return; }
            for (const position of win.positions) {
                Components
                    .get(MatrixComponent)
                    .getSymbol(position.x, position.y)
                    .setLayer(Layers.get("SymbolAnimations"));
            }
        });

        this.startContract = this.nextWin();
        return this.startContract;
    }

    public end(): Contract<void> {
        return new Contract<void>((resolve) => {
            this.wins.forEach((win) => {
                if (!win.positions) { return; }
                for (const position of win.positions) {
                    Components
                        .get(MatrixComponent)
                        .getSymbol(position.x, position.y)
                        .setLayer(Layers.get("MatrixContent"));
                }
            });
            this.startContract.cancel();
            this.wins = null;
            resolve(null);
        });
    }

    protected getWinsToAnimate(): Result[] {
        const record = gameState.getCurrentGame().getCurrentRecord() as SpinRecord;
        const wins: Array<BonusResult | SparkResult | SymbolWinResult> = record.getResultsOfType(SymbolWinResult);
        this.currentWin = 0;

        const bonusResults = record.getResultsOfType(BonusResult);
        const sparkResults = record.getResultsOfType(SparkResult);

        if (bonusResults.length > 0 && sparkResults.length > 0) {
            const bonusSparkResult = new BonusResult(bonusResults[0].id, true);
            bonusSparkResult.cashWon = bonusResults[0].cashWon;
            wins.push(sparkResults[0]);
        } else if (bonusResults.length > 0) {
            wins.push(bonusResults[0]);
        }

        return wins.sort((a, b) => b.cashWon - a.cashWon);
    }

    protected getCelebrationsForWin(win: any): Array<() => Contract<void>> {
        const celebrateWin: CelebrateWinComponent = Components.get(CelebrateWinComponent);

        if (win instanceof ScatterResult && win.cashWon > 0) {
            const linkedResult = win.linkedResult;
            if (linkedResult instanceof FreespinWinResult) {
                const formattedWin = Services.get(CurrencyService).format(win.cashWon)

                return [
                    () => celebrateWin.miscWin(win, Services.get(TranslationsService).get("freespins_win", { total_payout: formattedWin, triggered: linkedResult.freespinsWon }))
                ];
            } else {
                return [
                    () => celebrateWin.miscWin(win, "ways_win")
                ];
            }
        }

        if (win instanceof BonusResult || win instanceof ScatterResult) {
            return [
                () => celebrateWin.miscWin(win, "progressive_win_footer")
            ];
        }

        if (win instanceof SparkResult) {
            return [
                () => this.showWin(win),
                () => celebrateWin.miscWin(win, "progressive_win_footer")
            ];
        }

        const symbolDef = slotDefinition.getSymbolDefinition(win.symbolType);
        const symbolName = symbolDef ? symbolDef.name : "";

        if (win instanceof LineResult) {
            return [
                () => celebrateWin.lineWin(win, symbolName),
                () => this.showWin(win)
            ];
        }

        if (win instanceof FreespinScatterResult) {
            return [
                // TODO
                // () => celebrateWin.scatterWin(win),
                () => this.showWin(win)
            ];
        }

        return [
            () => celebrateWin.waysWin(win, symbolName),
            () => this.showWin(win)
        ];
    }

    protected showCelebrationForSymbols(symbols: SymbolSubcomponent[]): Contract<void> {
        return new Parallel<void>(symbols.map((symbol) => () => symbol.cycle()));
    }

    protected nextWin() {
        return new Contract<void>((resolve) => {
            const win = this.wins[this.currentWin];
            const celebrations = this.getCelebrationsForWin(win);

            // Set minimum animation duration.
            celebrations.push(() => Contract.getTimeoutContract(LoopWinsComponent.minCelebrationDuration));

            new Parallel(celebrations).then(() => {
                if (!this.wins || !this.wins.length) {
                    resolve(null);
                    return;
                }

                this.currentWin++;
                if (this.currentWin < this.wins.length) {
                    this.nextWin().then(resolve);
                } else {
                    resolve(null);
                }
            });
        });
    }

    protected showWin(win: WaysResult | SparkResult | ScatterResult | LineResult): Contract<void> {
        const winningSymbols = [];

        for (const position of win.positions) {
            winningSymbols.push(Components.get(MatrixComponent).getSymbol(position.x, position.y));
        }

        if (!winningSymbols.length) {
            return Contract.getTimeoutContract(LoopWinsComponent.minCelebrationDuration);
        }

        if(win instanceof ScatterResult) {
            Components.get(FooterComponent)?.showNoWin();
        }

        return this.showCelebrationForSymbols(winningSymbols);
    }
}
